import { appstr } from "@coreapp/utils/appstr";
import { useEffect, useRef, useState } from "react";
import { useRightSidebarContext } from "@/providers/RightSidebarProvider";
import { enumBlockerNextAction } from "@/providers/BlockerProvider";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { useBlockerContext } from "@/providers/BlockerProvider";
import { useTopicBlocker } from "@/hooks/useTopicBlocker";
import { createBoldToastMessage } from "@coreapp/utils/createBoldToastMessage";
import { enumRulePairLogicCondition, QRulePair } from "@coreapp/CoreApp/QRulePair";
import { QSharedFilterRFC } from "@coreapp/CoreApp/QSharedFilterRFC";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { QFilterBlock } from "@coreapp/CoreApp/QFilterBlock";
import { QHighlight } from "@coreapp/CoreApp/QHighlight";
import type { SharedFilterDoc } from "../FilterPanel/useSharedFilterController";
import { chartModeTestId } from "./chartModeTestId";
import {
  computeRulePairErrors,
  intersectErrors,
  UIRuleError
} from "@/appComponents/Blocks/Composites/RuleInput/ruleErrors";

export type RuleStatus = Record<number, boolean>;
type RuleValidationResult = { isValid: boolean; message: string };
export type HeaderCustomAction = {
  icon: string;
  onClick: () => void;
  buttonProps?: Record<string, any>;
};

type Props<T extends ChartRuleModifier> = {
  entity: enumChartRuleModifiers;
  ctrl: {
    handleStopEditing: () => void;
    handleCommit: (element: T) => void;
    handleStartEditing: (element: T) => void;
    handleNameChangeValidation: (currentName: string, name: string) => any;
  };
  editingElement: T;
  enableBlock: (element: T) => void;
  getRules: (element: T) => QRulePair[];
  sharedFilterDoc?: SharedFilterDoc;
  headerCustomAction?: HeaderCustomAction;
  onDelete?: () => void;
};

export enum enumChartRuleModifiers {
  Filter = "Filter",
  Highlight = "Highlight"
}

export interface ChartRuleModifier {
  GetName: () => string | null;
  SetName: (name: string) => void;
  Clone: () => any;
}

export function useChartRuleEditorController<T extends ChartRuleModifier>(props: Props<T>) {
  const {
    entity,
    ctrl,
    editingElement,
    enableBlock,
    getRules,
    sharedFilterDoc,
    headerCustomAction,
    onDelete
  } = props;
  const { shouldBlock } = useBlockerContext();
  const toastProv: any = useToast();
  const addToast = toastProv?.addToast;

  const [elementInitialName] = useState<string>(() => editingElement.GetName() ?? "");
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [savingShared, setSavingShared] = useState(false);
  const [ruleErrorsByUID, setRuleErrorsByUID] = useState<Record<number, UIRuleError>>({});
  const isDraggingRef = useRef(false);
  const [nameErrorShown, setNameErrorShown] = useState(false);
  const [ruleEditStatus, setRuleEditStatus] = useState<RuleStatus>(() => {
    const rules = getRules(editingElement);
    const isEditingRule = rules.reduce((acc: RuleStatus, rule: QRulePair) => {
      acc[rule.fUID] = true;
      return acc;
    }, {});

    if (rules.length === 1) isEditingRule[rules[0].fUID] = true;

    return isEditingRule;
  });

  // Logic for preventing mode change on the sidebar.
  const shouldBlockTabChange = () => {
    const isValid = getIsValid();
    return {
      doBlock: isDirty,

      alertParams: {
        title: appstr("Unsaved changes detected"),
        message: isValid ? appstr("saveconfirm3") : appstr("Errors in form"),
        confirmText: isValid ? appstr("Save Changes") : appstr("Resolve"),
        denyText: appstr("Discard Changes"),
        onConfirm: isValid ? saveBlock : () => enumBlockerNextAction.Cancel,
        onDeny: goBack
      }
    };
  };

  const getValidationResult = (): RuleValidationResult => {
    const nameValid = ctrl.handleNameChangeValidation(
      elementInitialName,
      editingElement.GetName() ?? ""
    ).result;

    if (!nameValid) {
      return {
        isValid: false,
        message: appstr("Fill all fields {entity}", { entity: appstr(entity).toLowerCase() })
      };
    }

    const rules: QRulePair[] = getRules(editingElement);
    if (rules.length === 0) {
      return {
        isValid: false,
        message: appstr("Fill all fields {entity}", { entity: appstr(entity).toLowerCase() })
      };
    }

    const invalidCriteriaInfo = QRulePair.GetFirstInvalidCriteria(rules);
    if (invalidCriteriaInfo) {
      return {
        isValid: false,
        message: invalidCriteriaInfo.message
      };
    }

    return { isValid: true, message: "" };
  };

  const getIsValid = () => getValidationResult().isValid;

  const isFilter = entity === enumChartRuleModifiers.Filter;
  const modeTestId = (suffix: string) => chartModeTestId(isFilter ? "filter" : "highlight", suffix);
  const { setCustomHeaderProps, closeSidebar } = useRightSidebarContext();
  useEffect(() => {
    setCustomHeaderProps(() => ({
      iconProps: {
        size: "xsmall",
        onClick: () => handleGoBack(),
        dataTestId: modeTestId("editor-back-btn")
      },
      icon: "chevron-left",
      title: appstr(`back to ${entity.toLowerCase()} list`),
      ...(headerCustomAction
        ? {
            headerActions: {
              customAction: headerCustomAction,
              closeAction: closeSidebar
            }
          }
        : {})
    }));
    return () => setCustomHeaderProps({});
  }, [isDirty, headerCustomAction]);

  useEffect(() => {
    setRuleErrorsByUID(prev => {
      if (isDraggingRef.current) return prev;
      if (Object.keys(prev).length === 0) return prev;
      const rules = getRules(editingElement);
      const rulesMap = new Map(rules.map((r: QRulePair) => [r.fUID, r]));
      const updated: Record<number, UIRuleError> = {};
      for (const key in prev) {
        const uid = Number(key);
        const rule = rulesMap.get(uid);
        if (!rule) continue;
        const intersected = intersectErrors(prev[uid], computeRulePairErrors(rule));
        if (intersected) updated[uid] = intersected;
      }
      return updated;
    });
  }, [editingElement]);

  const startDrag = () => {
    isDraggingRef.current = true;
  };
  const endDrag = () => {
    isDraggingRef.current = false;
  };

  const handleGoBack = (ignoreBlocker: boolean = false) => {
    if (!ignoreBlocker) {
      const onProceed = () => handleGoBack(true);
      if (shouldBlock("stopEditingCurrentFilter", onProceed)) {
        return;
      }
    }

    goBack();
  };
  useTopicBlocker("stopEditingCurrentFilter", shouldBlockTabChange);
  useTopicBlocker("rightSidebarChangeMode", shouldBlockTabChange);
  useTopicBlocker("refreshDocument", shouldBlockTabChange);

  const goBack = () => {
    setCustomHeaderProps({});
    ctrl.handleStopEditing();
  };

  const deleteAndGoBack = onDelete
    ? () => {
        onDelete();
        goBack();
        addToast({
          ...createBoldToastMessage("Entity deleted", { entity: editingElement.GetName() ?? "" }),
          status: TOAST_STATUS.SUCCESS,
          timeoutMS: 5000
        });
      }
    : undefined;

  const syncSharedRuntimeState = (docId: string, mode: "update" | "remove", definition?: any) => {
    const theDoc = GetDoc();
    const currentWsMgr = GetWSMgr();

    if (entity === enumChartRuleModifiers.Filter) {
      const filterMgr = theDoc?.GetFilterMgr();
      if (!filterMgr) return;
      const contextId = filterMgr.GetContextId();
      const sharedCtxId = `shared_${contextId}`;
      const blocks = filterMgr.GetFilterBlocks?.(sharedCtxId) || [];

      if (mode === "update" && definition) {
        const updated = blocks.map((b: any) => {
          if (b._sharedDocId !== docId) return b;
          const newBlock = new QFilterBlock();
          newBlock.LoadFromJSON({ ...definition, contextId: sharedCtxId, enabled: true });
          (newBlock as any)._sharedDocId = docId;
          return newBlock;
        });
        filterMgr.SetFilterBlocks(sharedCtxId, updated);
      } else {
        filterMgr.SetFilterBlocks(
          sharedCtxId,
          blocks.filter((b: any) => b._sharedDocId !== docId)
        );
      }
      if (currentWsMgr) {
        currentWsMgr.ApplyChartFilters();
        ReRenderCanvas();
      }
    } else {
      const highlightMgr = theDoc?.GetHighlightMgr();
      if (!highlightMgr) return;
      const shared = highlightMgr.GetSharedHighlights?.() || [];

      if (mode === "update" && definition) {
        const updated = shared.map((h: any) => {
          if (h._sharedDocId !== docId) return h;
          const newHL = new QHighlight();
          newHL.LoadFromJSON(definition);
          newHL.SetHighlightApplied(true);
          (newHL as any)._sharedDocId = docId;
          return newHL;
        });
        highlightMgr.SetSharedHighlights(updated);
      } else {
        highlightMgr.SetSharedHighlights(shared.filter((h: any) => h._sharedDocId !== docId));
      }
      if (currentWsMgr) {
        currentWsMgr.ApplyChartHighlights();
        currentWsMgr.SignalDocChanged();
      }
    }
  };

  const saveSharedFilter = async () => {
    const currentWsMgr = GetWSMgr();
    if (!currentWsMgr || !sharedFilterDoc) return;

    setSavingShared(true);
    try {
      const definition = (editingElement as any).OutputToJSON?.() ?? {};
      const raw = await QSharedFilterRFC.UpdateSharedFilter(
        currentWsMgr,
        sharedFilterDoc._id,
        definition
      );
      const response = typeof raw === "string" ? JSON.parse(raw) : raw;

      if (response?.success) {
        syncSharedRuntimeState(sharedFilterDoc._id, "update", definition);
        addToast({
          ...createBoldToastMessage("Entity changes saved", {
            entity: editingElement.GetName() ?? ""
          }),
          status: TOAST_STATUS.SUCCESS,
          timeoutMS: 5000
        });
        goBack();
      } else {
        addToast({
          message: response?.message || appstr("Shared filter update error"),
          status: TOAST_STATUS.ERROR,
          timeoutMS: 5000
        });
      }
    } catch (err) {
      console.error(`Failed to update shared ${entity.toLowerCase()}:`, err);
      addToast({
        message: appstr("Shared filter update error"),
        status: TOAST_STATUS.ERROR,
        timeoutMS: 5000
      });
    } finally {
      setSavingShared(false);
    }
  };

  const deleteSharedFilter = async () => {
    const currentWsMgr = GetWSMgr();
    if (!currentWsMgr || !sharedFilterDoc) return;

    try {
      const raw = await QSharedFilterRFC.DeleteSharedFilter(currentWsMgr, sharedFilterDoc._id);
      const response = typeof raw === "string" ? JSON.parse(raw) : raw;

      if (response?.success) {
        syncSharedRuntimeState(sharedFilterDoc._id, "remove");
        addToast({
          ...createBoldToastMessage("Entity deleted", {
            entity: editingElement.GetName() ?? ""
          }),
          status: TOAST_STATUS.SUCCESS,
          timeoutMS: 5000
        });
        goBack();
      } else {
        addToast({
          message: response?.message || appstr("Shared filter delete error"),
          status: TOAST_STATUS.ERROR,
          timeoutMS: 5000
        });
      }
    } catch (err) {
      console.error(`Failed to delete shared ${entity.toLowerCase()}:`, err);
      addToast({
        message: appstr("Shared filter delete error"),
        status: TOAST_STATUS.ERROR,
        timeoutMS: 5000
      });
    }
  };

  const triggerValidation = (): boolean => {
    const nameValid = ctrl.handleNameChangeValidation(
      elementInitialName,
      editingElement.GetName() ?? ""
    ).result;

    const rules = getRules(editingElement);
    const hasNoRules = rules.length === 0;
    const newRuleErrorsByUID: Record<number, UIRuleError> = {};
    for (const rule of rules) {
      const errors = computeRulePairErrors(rule);
      if (errors.hasError) newRuleErrorsByUID[rule.fUID] = errors;
    }
    const hasRuleErrors = Object.keys(newRuleErrorsByUID).length > 0;

    if (!nameValid || hasNoRules || hasRuleErrors) {
      if (!nameValid) setNameErrorShown(true);
      if (hasRuleErrors) {
        setRuleErrorsByUID(newRuleErrorsByUID);
        setRuleEditStatus(prev => {
          const next = { ...prev };
          for (const rule of rules) {
            if (newRuleErrorsByUID[rule.fUID]) next[rule.fUID] = true;
          }
          return next;
        });
      }
      return false;
    }

    return true;
  };

  const saveBlock = () => {
    if (!triggerValidation()) return;

    if (sharedFilterDoc) {
      saveSharedFilter();
      return;
    }

    enableBlock(editingElement);
    ctrl.handleCommit(editingElement);
    if (isNew) GetWSMgr()?.GetUsageTracker()?.ReportEvent("chart_mode.shared_filter.created");
    ctrl.handleStopEditing();
    setCustomHeaderProps({});

    const createKey =
      entity === enumChartRuleModifiers.Filter
        ? "Entity filter created"
        : "Entity highlight created";
    addToast({
      ...createBoldToastMessage(isNew ? createKey : "Entity changes saved", {
        entity: editingElement.GetName() ?? ""
      }),
      status: TOAST_STATUS.SUCCESS,
      timeoutMS: 5000
    });
  };

  // Small decorator for a common pattern of cloning, acting and then saving.
  const blockAction = (f: Function) => {
    return (...args: any[]) => {
      const prevElement = editingElement.Clone();
      f(prevElement, ...args);

      ctrl.handleStartEditing(prevElement);
      setIsDirty(true);
    };
  };

  const _changeName = blockAction((element: T, name: string) => element.SetName(name));
  const changeName = (name: string) => {
    _changeName(name);
    setNameErrorShown(false);
  };

  const insertRuleAt = blockAction(
    (
      updatingBlock: T,
      ruleIndex: number,
      rule: QRulePair,
      begginingOfOrBlock: boolean,
      endOfOrBlock: boolean
    ) => {
      const rules = getRules(updatingBlock);

      const secPrevRule = rules[ruleIndex - 2];
      const prevRule = rules[ruleIndex - 1];
      const nextRule = rules[ruleIndex];

      // Between ors is always an or
      if (prevRule?.fCondition === enumRulePairLogicCondition.or) {
        rule.fCondition = enumRulePairLogicCondition.or;
        if (!nextRule) {
          rule.fCondition = enumRulePairLogicCondition.and;
        }
      } else {
        if (endOfOrBlock) {
          if (secPrevRule?.fCondition === enumRulePairLogicCondition.or) {
            prevRule.fCondition = enumRulePairLogicCondition.or;
            rule.fCondition = enumRulePairLogicCondition.and;
          }
          rule.fCondition = enumRulePairLogicCondition.and;
        } else if (begginingOfOrBlock) {
          rule.fCondition =
            nextRule?.fCondition === enumRulePairLogicCondition.or
              ? enumRulePairLogicCondition.or
              : enumRulePairLogicCondition.and;
        } else {
          rule.fCondition = enumRulePairLogicCondition.and;
        }
      }

      rules.splice(ruleIndex, 0, rule);
    }
  );

  const toggleRuleEditStatus = (rule: QRulePair) => {
    setRuleEditStatus(prevStatus => {
      const newStatus = { ...prevStatus };
      newStatus[rule.fUID] = !newStatus[rule.fUID];
      return newStatus;
    });
  };

  const isInEmptyState = () => {
    const rules = getRules(editingElement);
    return rules.length == 1 && !rules[0].GetFieldName();
  };

  // Can't have an empty initial name, unless it's a new entity.
  const isNew = elementInitialName === null || elementInitialName === "";

  return {
    shouldBlockTabChange,
    blockAction,
    changeName,
    setRuleEditStatus,
    entity,
    editingElement,
    getRules,
    sharedFilterDoc,
    handleNameChangeValidation: ctrl.handleNameChangeValidation,
    elementInitialName,
    bannerDismissed,
    setBannerDismissed,
    savingShared,
    ruleEditStatus,
    toggleRuleEditStatus,
    insertRuleAt,
    saveBlock,
    triggerValidation,
    deleteSharedFilter,
    isInEmptyState,
    handleGoBack,
    isNew,
    deleteAndGoBack,
    ruleErrorsByUID,
    nameErrorShown,
    startDrag,
    endDrag
  };
}
